import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Plus, Sparkles, TrendingUp, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/apiClient";
import { toast } from "sonner";

const STAGE_LABEL = { new: "New", qualified: "Qualified", demo_scheduled: "Demo scheduled", proposal: "Proposal", negotiation: "Negotiation", contract: "Contract", active: "Active", lost: "Lost" };
const TYPE_COLOR = { family: "bg-teal-50 text-teal-700", business: "bg-blue-50 text-blue-700", government: "bg-purple-50 text-purple-700" };

export default function Crm() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [stages, setStages] = useState([]);
  const [type, setType] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", type: "family", organization: "", care_need: "", deal_value: 0 });

  const load = useCallback(() => {
    const params = type ? { type } : {};
    api.get("/crm/leads", { params }).then((r) => { setLeads(r.data.leads); setStages(r.data.stages); }).catch(() => toast.error("Could not load leads"));
  }, [type]);

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate("/login"); return; }
    if (!["admin", "executive", "manager", "staff"].includes(user.role)) { navigate("/dashboard"); return; }
    load();
  }, [user, loading, navigate, load]);

  const updateStage = async (id, stage) => {
    try { await api.patch(`/crm/leads/${id}`, { stage }); toast.success("Stage updated"); load(); }
    catch { toast.error("Update failed"); }
  };
  const aiDraft = async (id) => {
    toast.loading("Drafting follow-up…", { id: "d" });
    try { await api.post(`/crm/leads/${id}/ai-draft`); toast.success("Draft prepared — review it in the Approval Center", { id: "d" }); }
    catch { toast.error("Could not draft", { id: "d" }); }
  };
  const addLead = async (e) => {
    e.preventDefault();
    try { await api.post("/crm/leads", { ...form, deal_value: Number(form.deal_value) || 0 }); toast.success("Lead created"); setShowAdd(false); setForm({ name: "", email: "", phone: "", type: "family", organization: "", care_need: "", deal_value: 0 }); load(); }
    catch { toast.error("Could not create lead"); }
  };

  if (loading || !user) return <div className="py-32 grid place-items-center"><Loader2 className="w-8 h-8 animate-spin text-blue-800" /></div>;

  const grouped = stages.map((s) => ({ stage: s, items: leads.filter((l) => l.stage === s) }));

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="crm-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2 mb-1"><TrendingUp className="w-5 h-5 text-teal-500" /><p className="text-xs uppercase tracking-[0.16em] text-teal-600 font-semibold">T5 Sales Agent · CRM</p></div>
          <h1 className="font-display text-3xl lg:text-4xl font-bold text-blue-950">Pipeline</h1>
        </div>
        <div className="flex gap-2">
          <select value={type} onChange={(e) => setType(e.target.value)} data-testid="crm-type-filter" className="h-10 rounded-lg border border-slate-200 px-3 text-sm">
            <option value="">All types</option><option value="family">Family (B2C)</option><option value="business">Business (B2B)</option><option value="government">Government (B2G)</option>
          </select>
          <Button onClick={() => setShowAdd(!showAdd)} data-testid="crm-add-toggle" className="bg-teal-600 hover:bg-teal-700 gap-1.5"><Plus className="w-4 h-4" /> Add lead</Button>
        </div>
      </div>

      {showAdd && (
        <form onSubmit={addLead} className="mt-5 bg-white border border-slate-200 rounded-2xl p-5 grid sm:grid-cols-3 gap-3" data-testid="crm-add-form">
          <Input required placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="crm-name" />
          <Input required type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="crm-email" />
          <Input placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="crm-phone" />
          <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="h-10 rounded-lg border border-slate-200 px-3 text-sm" data-testid="crm-type">
            <option value="family">Family</option><option value="business">Business</option><option value="government">Government</option>
          </select>
          <Input placeholder="Organization (B2B/B2G)" value={form.organization} onChange={(e) => setForm({ ...form, organization: e.target.value })} data-testid="crm-org" />
          <Input type="number" placeholder="Deal value ($)" value={form.deal_value} onChange={(e) => setForm({ ...form, deal_value: e.target.value })} data-testid="crm-value" />
          <Input className="sm:col-span-2" placeholder="Care need / notes" value={form.care_need} onChange={(e) => setForm({ ...form, care_need: e.target.value })} data-testid="crm-need" />
          <Button type="submit" data-testid="crm-add-submit" className="bg-blue-900 hover:bg-blue-950">Create</Button>
        </form>
      )}

      <div className="mt-6 grid gap-4 md:grid-cols-2 xl:grid-cols-4" data-testid="crm-board">
        {grouped.map(({ stage, items }) => (
          <div key={stage} className="bg-slate-50 rounded-2xl p-3" data-testid={`crm-col-${stage}`}>
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2 flex items-center justify-between">{STAGE_LABEL[stage]} <span className="bg-white rounded-full px-2 text-slate-400">{items.length}</span></p>
            <div className="space-y-2">
              {items.map((l) => (
                <div key={l.id} className="bg-white border border-slate-200 rounded-xl p-3 shadow-sm" data-testid={`crm-lead-${l.id}`}>
                  <div className="flex items-center justify-between">
                    <span className={`text-[10px] uppercase font-semibold px-2 py-0.5 rounded-full ${TYPE_COLOR[l.type]}`}>{l.type}</span>
                    <span className="text-[10px] font-semibold text-slate-400">score {l.score}</span>
                  </div>
                  <p className="font-semibold text-blue-950 text-sm mt-1.5">{l.name}</p>
                  {l.organization && <p className="text-xs text-slate-500">{l.organization}</p>}
                  <p className="text-xs text-slate-600 mt-1 line-clamp-2">{l.care_need || l.message}</p>
                  {l.deal_value > 0 && <p className="text-xs font-semibold text-teal-600 mt-1">${Number(l.deal_value).toLocaleString()}</p>}
                  <div className="flex gap-1.5 mt-2">
                    <select value={l.stage} onChange={(e) => updateStage(l.id, e.target.value)} data-testid={`crm-stage-${l.id}`} className="flex-1 h-8 rounded-lg border border-slate-200 px-2 text-xs">
                      {stages.map((s) => <option key={s} value={s}>{STAGE_LABEL[s]}</option>)}
                    </select>
                    {l.type === "family" && <button onClick={() => navigate(`/ops/match/${l.id}`)} data-testid={`crm-match-${l.id}`} title="AI caregiver matches" className="grid place-items-center w-8 h-8 rounded-lg bg-blue-50 text-blue-700 hover:bg-blue-100"><Users className="w-4 h-4" /></button>}
                    <button onClick={() => aiDraft(l.id)} data-testid={`crm-aidraft-${l.id}`} title="AI draft follow-up" className="grid place-items-center w-8 h-8 rounded-lg bg-teal-50 text-teal-600 hover:bg-teal-100"><Sparkles className="w-4 h-4" /></button>
                  </div>
                </div>
              ))}
              {items.length === 0 && <p className="text-xs text-slate-300 text-center py-4">—</p>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
